import { useEffect } from "react"
import TagManager from "react-gtm-module"

const CONSENT_EVENTS = [
  "CookiebotOnConsentReady",
  "CookiebotOnAccept",
  "CookiebotOnDecline",
]
// Analytics cookies that must be cleared when statistics consent is absent.
const ANALYTICS_COOKIES = ["_gid", "_ga", "_dc_gtm_UA-130711147-3"]

/**
 * Wires Cookiebot consent to GTM. Cookiebot itself loads standalone (see
 * `site-metadata.js`); this only reacts to its consent state:
 *
 * - Pushes a 4-category `cookie_consent_update` event to the dataLayer so GTM
 *   (Consent Mode V2 Basic) can gate each tag per category.
 * - Loads GTM as soon as the visitor grants ANY non-essential category
 *   (`preferences` | `statistics` | `marketing`) — unless `noAnalytics` is
 *   set. Declining everything (only `necessary`) means GTM is never loaded.
 * - Clears stale analytics cookies when `statistics` consent is absent.
 *
 * The GTM container is taken **only** from `GATSBY_COOKIEBOT`-style env config:
 * `GATSBY_GMT_ID` (per site). If it is not set, GTM is not loaded at all — no
 * hardcoded fallback. `GATSBY_GTM_AUTH` / `GATSBY_GTM_PREVIEW` are optional and
 * select a non-Live GTM Environment (e.g. to test a workspace version on a
 * preview domain without publishing to prod).
 */
const cookieBot = (noAnalytics) => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined

    const gtmId = process.env.GATSBY_GMT_ID
    const gtmAuth = process.env.GATSBY_GTM_AUTH
    const gtmPreview = process.env.GATSBY_GTM_PREVIEW

    const loadGtm = () => {
      // No container configured -> do not load GTM (env-driven, no fallback).
      if (!gtmId) return
      if (!noAnalytics) {
        const args = { gtmId }
        if (gtmAuth) args.auth = gtmAuth
        if (gtmPreview) args.preview = gtmPreview
        TagManager.initialize(args)
      }
    }

    const deleteCookie = (name) => {
      document.cookie = `${name}=;Path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;`
    }

    const syncConsent = () => {
      const consent = window.Cookiebot?.consent || {}

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: "cookie_consent_update",
        consent_necessary: !!consent.necessary,
        consent_preferences: !!consent.preferences,
        consent_statistics: !!consent.statistics,
        consent_marketing: !!consent.marketing,
      })

      // Load GTM as soon as ANY non-essential category is granted. GTM is only
      // the tag dispatcher; Consent Mode V2 inside GTM still gates each tag per
      // category (statistics ← statistics, marketing ← marketing). Gating the
      // load on `preferences` alone made "Auswahl erlauben" with Statistik-only
      // consent behave identically to "Nur funktionsnotwendige": GTM never
      // loaded, so analytics_storage=granted had no effect.
      if (consent.preferences || consent.statistics || consent.marketing) {
        loadGtm()
      }

      if (!consent.statistics) {
        ANALYTICS_COOKIES.forEach(deleteCookie)
      }
    }

    CONSENT_EVENTS.forEach((event) =>
      window.addEventListener(event, syncConsent)
    )

    // Consent may have been decided before this effect ran (returning visitor).
    if (window.Cookiebot?.consent) syncConsent()

    return () =>
      CONSENT_EVENTS.forEach((event) =>
        window.removeEventListener(event, syncConsent)
      )
  }, [noAnalytics])

  useEffect(() => {
    const iOS =
      typeof navigator !== "undefined" &&
      !!navigator.platform &&
      /iPad|iPhone|iPod/.test(navigator.platform)
    if (iOS) {
      document.body.scroll(0, 0)
    }
  }, [])
}

export default cookieBot
